/**
 * Authenticate a peer's published account (X25519) public key against their
 * identity key (PKA site A1/A4). The account key is served by the untrusted
 * server; the signature only holds if it is by the peer's *own* identity key,
 * over exactly this account key, and bound to the peer's own userId. A malicious
 * server that pairs a valid signature with a substituted key, or reuses another
 * user's signature, fails these checks.
 */
import { verifyKey } from '@heybesafe/shared';
import type { SignedKey } from '@heybesafe/shared';
import { base64UrlToBytes } from '../crypto/base64url';

export interface AccountKeyClaim {
  userId: string;
  accountPublicKey?: string;
  identityPublicKey?: string;
  accountPublicKeySig?: SignedKey;
}

/**
 * True iff the account-key signature is a valid PKA claim: signed by the peer's
 * identity key, over this account key, for this userId. Callers still pin the
 * identity (TOFU) before trusting the key — this only proves the server didn't
 * fabricate the pairing.
 */
export function accountKeyAuthentic(c: AccountKeyClaim): boolean {
  if (!c.accountPublicKey || !c.identityPublicKey || !c.accountPublicKeySig) return false;
  const sig = c.accountPublicKeySig;
  if (
    sig.keyType !== 'account-x25519'
    || sig.signerId !== c.userId
    || sig.subject !== c.userId
    || sig.key !== c.accountPublicKey
  ) return false;
  return verifyKey(base64UrlToBytes(c.identityPublicKey), sig);
}
